import { Match, Schema } from 'effect';

export const dateRangeSchema = Schema.Struct({ from: Schema.Date, to: Schema.Date });
export type DateRange = typeof dateRangeSchema.Type;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const startOfMonth = (date: Date, offset = 0): Date =>
  new Date(date.getFullYear(), date.getMonth() + offset, 1);

const unitDays: Readonly<Record<string, number>> = { d: 1, w: 7, m: 30, y: 365 };

const lastDaysPattern = /^last (\d+) (day|days|week|weeks|month|months)$/;
const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const timestampPattern =
  /^(\d{4}-\d{2}-\d{2})t\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:z|[+-]\d{2}:\d{2})$/i;

const daysInUnit = (unit: string): number =>
  Match.value(unit).pipe(
    Match.when(Match.is('week', 'weeks'), () => 7),
    Match.when(Match.is('month', 'months'), () => 30),
    Match.orElse(() => 1),
  );

type Resolver = (match: RegExpExecArray, now: Date) => DateRange | null;

const dayRange = (from: Date, days: number): DateRange => ({ from, to: addDays(from, days) });

const resolvers: readonly { readonly pattern: RegExp; readonly resolve: Resolver }[] = [
  { pattern: /^today$/, resolve: (_m, now) => dayRange(startOfDay(now), 1) },
  { pattern: /^yesterday$/, resolve: (_m, now) => dayRange(addDays(startOfDay(now), -1), 1) },
  {
    pattern: /^this month$/,
    resolve: (_m, now) => ({ from: startOfMonth(now), to: startOfMonth(now, 1) }),
  },
  {
    pattern: /^last month$/,
    resolve: (_m, now) => ({ from: startOfMonth(now, -1), to: startOfMonth(now) }),
  },
  {
    pattern: /^-(\d+)([dwmy])$/,
    resolve: (m, now) => ({
      from: addDays(startOfDay(now), -Number(m[1]) * unitDays[m[2]]),
      to: addDays(startOfDay(now), 1),
    }),
  },
  {
    pattern: lastDaysPattern,
    resolve: (m, now) => {
      const unit = daysInUnit(m[2]);
      return {
        from: addDays(startOfDay(now), -Number(m[1]) * unit),
        to: addDays(startOfDay(now), 1),
      };
    },
  },
  {
    pattern: isoDatePattern,
    resolve: (m) => {
      const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return day.getFullYear() !== Number(m[1]) ||
        day.getMonth() !== Number(m[2]) - 1 ||
        day.getDate() !== Number(m[3])
        ? null
        : dayRange(day, 1);
    },
  },
];

export const parseDateValue = (raw: string, now: Date): DateRange | null => {
  const text = raw.trim().toLowerCase();
  const timestamp = timestampPattern.exec(text);
  if (timestamp !== null) {
    if (parseDateValue(timestamp[1], now) === null) return null;
    const instant = new Date(raw.trim().toUpperCase());
    return Number.isFinite(instant.getTime()) ? { from: instant, to: instant } : null;
  }
  const hit = resolvers.map((r) => ({ r, m: r.pattern.exec(text) })).find((x) => x.m !== null);
  const range = hit === undefined || hit.m === null ? null : hit.r.resolve(hit.m, now);
  return range === null ||
    !Number.isFinite(range.from.getTime()) ||
    !Number.isFinite(range.to.getTime())
    ? null
    : range;
};

export const isDateValue = (raw: string): boolean =>
  parseDateValue(raw, new Date(2000, 0, 1)) !== null;

export const dateValueSchema = Schema.String.check(
  Schema.makeFilter(isDateValue, {
    message: 'Enter a calendar date, ISO timestamp with timezone, or a supported relative date',
  }),
).pipe(Schema.brand('QueryDate'));
